import * as React from 'react'
import * as API from '../lib/api'
import { PurchaseOption } from '../lib/purchase_option'
import { TaxInformation } from '../lib/tax_information'

interface IProps {
  purchaseOptions: PurchaseOption[]
  selectedPurchaseOptionName: API.PurchaseOptionNameType
  onPurchaseOptionChange: (name: API.PurchaseOptionNameType) => void
  // Drives the tax line's loading/failed state, its label, and the failure copy;
  // this component computes no tax or totals of its own.
  taxInformation: TaxInformation
  // Server-computed totals for both periods. Absent until the first sales-tax
  // round-trip resolves; the selected period is picked by name.
  totals?: API.IOrderTotals
  trial: boolean
}

export class OrderDetails extends React.Component<IProps, object> {
  public render() {
    const { selectedPurchaseOptionName } = this.props
    const option = this.selectedOption()

    // A transient name/options mismatch during a toggle shouldn't crash the
    // rail; render nothing so the subtree recovers on the next props update.
    if (!option) {
      return null
    }

    return (
      <section className="order-details" data-testid="order-details">
        {this.renderHeader(option)}
        {this.renderPrices(option)}
        {this.renderSubtotalAndTax(option)}
        {selectedPurchaseOptionName !== 'annual' && this.renderAnnualEquivalencyTotalAndInstallmentAmount(option)}
        {this.renderTotal(option)}
        {this.renderTaxNote()}
      </section>
    )
  }

  protected renderHeader(option: PurchaseOption) {
    const { membershipPlan } = option

    return (
      <header className="order-details__header">
        <p className="order-details__kicker" data-testid="order-details-kicker">Order Details</p>
        <div className="order-details__title-row">
          <h2 className="order-details__plan-name">{membershipPlan.name} Plan</h2>
          {this.renderToggle()}
        </div>
        <p className="order-details__plan-subtitle" data-testid="order-details-plan-subtitle">
          {this.planSubtitle(option)}
        </p>
      </header>
    )
  }

  protected planSubtitle(option: PurchaseOption): string {
    const perPayment = this.formatPrice(option.fullPrice())

    if (option.name === 'annual') {
      return `Annual payment of ${perPayment}`
    }

    return `${option.paymentDetails.numberOfPayments} installments of ${perPayment}/month`
  }

  // Keeps radiogroup semantics rather than `role="switch"`: a switch announces
  // "on/off", which can't convey "Monthly vs Annual", and the radiogroup
  // generalizes past two periods. The sliding knob is decorative — its side is
  // driven by the CSS `--knob-*` modifier below, not a separate control.
  protected renderToggle() {
    const { purchaseOptions, selectedPurchaseOptionName } = this.props
    const isSwitch = purchaseOptions.length === 2

    // Keyed off the selected label's rendered position, not its period name:
    // the host decides the order, so a name-based rule would put the knob on the
    // wrong side whenever the order isn't Monthly-then-Annual.
    const selectedIndex = purchaseOptions.findIndex((o) => o.name === selectedPurchaseOptionName)
    const knobSide = selectedIndex >= 1 ? 'end' : 'start'

    return (
      <div
        className={`order-details__toggle order-details__toggle--knob-${knobSide}`}
        role="radiogroup"
        aria-label="Billing period"
      >
        {purchaseOptions.map((option, index) => {
          const label = this.renderToggleOption(option, index)

          // The track is a mouse affordance layered over the real radios (which
          // stay the keyboard/AT interface), so it is aria-hidden.
          if (isSwitch && index === 1) {
            return (
              <React.Fragment key={option.name}>
                <span
                  className="order-details__toggle-track"
                  data-testid="order-details-toggle-track"
                  aria-hidden="true"
                  onClick={this.handleToggle}
                >
                  <span className="order-details__toggle-knob" />
                </span>
                {label}
              </React.Fragment>
            )
          }

          return label
        })}
      </div>
    )
  }

  protected renderToggleOption(option: PurchaseOption, index: number) {
    const active = option.name === this.props.selectedPurchaseOptionName

    return (
      <button
        key={option.name}
        ref={(el) => { this.optionRefs[option.name] = el }}
        type="button"
        role="radio"
        className={`order-details__toggle-option${active ? ' order-details__toggle-option--active' : ''}`}
        data-testid={`order-details-option-${option.name}`}
        aria-checked={active}
        tabIndex={active ? 0 : -1}
        onClick={() => this.handleSelect(option.name)}
        onKeyDown={(event) => this.handleArrow(event, index)}
      >
        {option.label}
      </button>
    )
  }

  // Keyed by option name so an arrow key can focus the newly-selected radio
  // (roving-tabindex pattern).
  protected optionRefs: { [name: string]: HTMLButtonElement | null } = {}

  protected handleArrow(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown'
    const backward = event.key === 'ArrowLeft' || event.key === 'ArrowUp'

    if (!forward && !backward) {
      return
    }

    event.preventDefault()

    const { purchaseOptions } = this.props
    const count = purchaseOptions.length
    const delta = forward ? 1 : -1
    const next = purchaseOptions[(index + delta + count) % count]

    this.handleSelect(next.name)
    const button = this.optionRefs[next.name]
    if (button) {
      button.focus()
    }
  }

  protected renderPrices(option: PurchaseOption) {
    const { membershipPlan, paymentDetails, paymentOption } = option
    const discounted = paymentOption.priceBreakdown.adjustments.some(a => a.value !== 0)

    return (
      <ul className="order-details__prices" data-testid="order-details-prices">
        {this.priceLine(`${membershipPlan.name} Plan`, paymentOption.fullPrice)}
        {
          discounted ?
            paymentOption.priceBreakdown.adjustments.filter(({ value }) => value !== 0).map(({ label, value }) => this.priceLine(label, -value)) :
            null
        }
      </ul>
    )
  }

  protected renderSubtotalAndTax(option: PurchaseOption) {
    const { paymentOption } = option

    return (
      <ul className="order-details__subtotal-and-tax" data-testid="order-details-subtotal-and-tax">
        {this.priceLine('Subtotal', paymentOption.priceBreakdown.calculated_price)}
        {this.renderTaxLine()}
      </ul>
    )
  }

  protected renderTaxLine() {
    const { taxInformation } = this.props

    // Checked before `isLoaded`: a failed request is also "not loaded", but it
    // will never resolve, so "Calculating…" would be a permanent lie.
    if (taxInformation.hasFailed) {
      return this.unavailableLine(taxInformation.salesTaxLabel)
    }

    if (!taxInformation.isLoaded) {
      return (
        <li className="order-details__price-line">
          <span className="order-details__price-label">{taxInformation.salesTaxLabel}</span>
          <span className="order-details__price-amount">Calculating&hellip;</span>
        </li>
      )
    }

    const totals = this.selectedTotals()

    if (!totals) {
      return this.unavailableLine(taxInformation.salesTaxLabel)
    }

    return this.priceLine(totals.tax_label, totals.tax)
  }

  protected renderAnnualEquivalencyTotalAndInstallmentAmount(option: PurchaseOption) {
    const totals = this.selectedTotals()

    if (!totals) {
      return null
    }

    return (
      <ul className="order-details__total-and-installment-amount" data-testid="order-details-total-and-installment-amount">
        {this.priceLine('Total Due', totals.total)}
        {this.priceLine(this.afterTrialDescription(option), totals.installment_amount ?? 0, true)}
      </ul>
    )
  }

  protected renderTotal(option: PurchaseOption) {
    return (
      <div className="order-details__total-block">
        {this.renderAfterTrial(option)}
        <div className="order-details__total" data-testid="order-details-total">
          <span className="order-details__total-label">Total Due Today</span>
          <span className="order-details__total-amount">{this.totalDueTodayText()}</span>
        </div>
        {this.renderTotalsErrors()}
      </div>
    )
  }

  // The server explains why it could not price the order (e.g. a 422's
  // UNPRICEABLE_ERROR). Without this the error contract reaches no user.
  protected renderTotalsErrors() {
    const { taxInformation } = this.props

    if (taxInformation.errors.length === 0) {
      return null
    }

    return (
      <ul className="order-details__totals-errors" data-testid="order-details-totals-errors" role="alert">
        {taxInformation.errors.map((error) => (
          <li key={error} className="order-details__totals-error">{error}</li>
        ))}
      </ul>
    )
  }

  protected renderAfterTrial(option: PurchaseOption) {
    const totals = this.selectedTotals()

    if (!this.props.trial || option.name !== 'annual' || !totals) {
      return null
    }

    return (
      <div className="order-details__after-trial" data-testid="order-details-after-trial">
        <ul className="order-details__total-and-installment-amount" data-testid="order-details-total-and-installment-amount">
          {this.priceLine(this.afterTrialDescription(option), totals.total, true)}
        </ul>
      </div>
    )
  }

  protected afterTrialDescription(option: PurchaseOption): string {
    const { paymentDetails: { numberOfPayments } } = option

    if (option.name === 'annual') {
      return 'Annual Payment after trial'
    } else {
      return `${numberOfPayments} Monthly Payments of`
    }
  }

  // Explains the `*` carried by the Sales Tax line.
  protected renderTaxNote() {
    return (
      <p className="order-details__footnote" data-testid="order-details-tax-note">
        *Tax exempt? Complete checkout, and you can submit your exemption certificate for a sales tax refund.
      </p>
    )
  }

  protected handleSelect(name: API.PurchaseOptionNameType) {
    if (name === this.props.selectedPurchaseOptionName) {
      return
    }
    this.props.onPurchaseOptionChange(name)
  }

  protected handleToggle = () => {
    const { purchaseOptions, selectedPurchaseOptionName } = this.props
    const other = purchaseOptions.find((o) => o.name !== selectedPurchaseOptionName)
    if (other) {
      this.handleSelect(other.name)
    }
  }

  protected priceLine(label: string, amount: number, bold: boolean = false) {
    return (
      <li key={label} className={`order-details__price-line ${bold ? 'order-details__price-line-bold' : ''}`}>
        <span className="order-details__price-label">{label}</span>
        <span className="order-details__price-amount">{this.formatPrice(amount)}</span>
      </li>
    )
  }

  protected unavailableLine(label: string) {
    return (
      <li key={label} className="order-details__price-line">
        <span className="order-details__price-label">{label}</span>
        <span className="order-details__price-amount">Unavailable</span>
      </li>
    )
  }

  protected totalDueTodayText(): string {
    const { taxInformation } = this.props

    if (taxInformation.hasFailed) {
      return 'Unavailable'
    }

    const totals = this.selectedTotals()

    if (totals) {
      return this.formatPrice(totals.total_due_today)
    }

    // Only claim a calculation is running while one actually is: a resolved
    // request that produced no totals is as final as a failed one.
    return taxInformation.isLoaded ? 'Unavailable' : 'Calculating…'
  }

  protected selectedOption(): PurchaseOption | undefined {
    const { purchaseOptions, selectedPurchaseOptionName } = this.props

    return purchaseOptions.find((o) => o.name === selectedPurchaseOptionName)
  }

  protected selectedTotals(): API.IPurchaseOptionTotals | undefined {
    const { totals, selectedPurchaseOptionName } = this.props

    return totals?.[selectedPurchaseOptionName]
  }

  protected formatPrice(price: number): string {
    // Safe-guard against accidental undefined/null run-time values
    const safePrice = price ?? 0;

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });

    // Extract the parts of the number
    const parts = formatter.formatToParts(safePrice);

    // Re-assemble the remaining number formatting (integers, commas, decimals)
    const numberValue = parts
      .filter(p => !['minusSign', 'currency', 'literal'].includes(p.type))
      .map(p => p.value)
      .join('');

    // Explicitly force the negative sign to the left of the dollar sign
    if (safePrice < 0) {
      return `-$${numberValue}`;
    }
    
    return `$${numberValue}`;
  }
}
